import {
	Button,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import Accessory from 'src/types/shared/accessory';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import {
	useQuoteDispatch,
	useQuoteState,
} from 'src/stateProviders/QuoteStateProvider';

const AccessoryCard: React.FC<{
	accessory: Accessory;
	quantity?: number | null;
	selectedOptions: number[];
	editMode?: boolean;
	setSelectedAccessory: Dispatch<
		SetStateAction<{ accessory: Accessory | null; quantity: number | null }>
	>;
}> = ({
	accessory,
	selectedOptions,
	quantity: qty,
	editMode,
	setSelectedAccessory,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [quantity, setQuantity] = useState(qty?.toString() || '1');
	useEffect(() => {
		setQuantity(qty?.toString() || '1');
	}, [qty, accessory._id]);
	const { options } = useQuoteState();
	const quoteDispatch = useQuoteDispatch();
	const quantityInt = Math.max(0, Math.floor(Number(quantity) || 0));

	// Prevent negative input and disallow exponential and minus chars.
	const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (value === '') {
			setQuantity('');
			return;
		}
		const numericValue = Number(value);
		if (!Number.isNaN(numericValue)) {
			setQuantity(numericValue < 0 ? '0' : value);
		}
	};

	const addAccessory = () => {
		const updatedOptions = [...options];

		updatedOptions.forEach((option, index) => {
			if (selectedOptions.includes(index)) {
				const accessoryIndex = option.accessories.findIndex(
					(acc) => acc.id == accessory._id
				);

				if (accessoryIndex > -1) {
					const existingAccessory = option.accessories[accessoryIndex];

					option.accessories.splice(accessoryIndex, 1, {
						...existingAccessory,
						quantity: existingAccessory.quantity + quantityInt,
					});
				} else {
					option.accessories = [
						...option.accessories,
						{
							id: accessory._id || '',
							quantity: quantityInt,
							name: accessory.name,
						},
					];
				}
			}
		});

		quoteDispatch({ options: updatedOptions });
		setQuantity('1');
	};

	const updateAccessory = () => {
		const updatedOptions = [...options];

		updatedOptions.forEach((option, index) => {
			if (selectedOptions.includes(index)) {
				const accessoryIndex = option.accessories.findIndex(
					(acc) => acc.id == accessory._id
				);

				if (accessoryIndex > -1) {
					const existingAccessory = option.accessories[accessoryIndex];

					option.accessories.splice(accessoryIndex, 1, {
						...existingAccessory,
						quantity: quantityInt,
					});
				}
			}
		});

		quoteDispatch({ options: updatedOptions });
		setSelectedAccessory({ accessory: null, quantity: null });
	};

	return (
		<Stack
			sx={{
				border: '1px solid #D1D5DB',
				borderRadius: '10px',
				padding: '10px',
				width: '100%',
			}}
		>
			<Stack
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '15px',
				}}
			>
				<Stack>
					<Typography
						sx={{
							color: '#202937',
							fontFamily: 'Inter',
							fontSize: '16px',
							fontStyle: 'normal',
							fontWeight: 600,
							lineHeight: '21px',
						}}
					>
						{accessory.name}
					</Typography>
					<Typography
						sx={{
							color: '#4A5565',
							fontFamily: 'Inter',
							fontSize: '12px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '18px',
						}}
					>
						{accessory.description}
					</Typography>
				</Stack>

				<Stack
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: '10px',
					}}
				>
					<TextField
						type="number"
						value={quantity}
						onChange={handleQuantityChange}
						onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
							if (e.key === '-' || e.key === 'e' || e.key === 'E') {
								e.preventDefault();
							}
						}}
						data-testid="dumpster-cost-input"
						sx={{ width: '120px' }}
						slotProps={{
							htmlInput: { step: 1, inputMode: 'numeric' },
							input: {
								endAdornment: (
									<InputAdornment
										position="end"
										sx={{
											marginRight: '-10px',
										}}
									>
										<Typography color="gray.500" fontSize="12px">
											{
												{
													'Per Linear Foot': 'Linear ft',
													'Per Square Foot': 'Square ft',
													'Per Accessory': 'Quantity',
												}[accessory.calculationType]
											}
										</Typography>
									</InputAdornment>
								),
							},
						}}
					/>

					{!editMode && (
						<Button
							sx={{ backgroundColor: 'primary.main', color: 'white' }}
							startIcon={<AddIcon />}
							onClick={addAccessory}
						>
							Add
						</Button>
					)}

					{editMode && (
						<>
							<Button
								sx={{ color: 'black' }}
								onClick={() =>
									setSelectedAccessory({ accessory: null, quantity: null })
								}
							>
								Cancel
							</Button>
							<Button
								sx={{ backgroundColor: 'primary.main', color: 'white' }}
								onClick={updateAccessory}
							>
								Update
							</Button>
						</>
					)}
				</Stack>
			</Stack>

			<Stack
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Typography
					sx={{
						color: '#4A5565',
						fontFamily: 'Inter',
						fontSize: '14px',
						fontStyle: 'normal',
						fontWeight: 600,
						lineHeight: '18px',
					}}
				>
					Included Materials & Labor ({Math.max(0, accessory.materials.length)})
				</Typography>

				{!isExpanded && (
					<IconButton onClick={() => setIsExpanded(true)}>
						<ExpandMoreIcon sx={{ color: '#4A5565' }} />
					</IconButton>
				)}

				{isExpanded && (
					<IconButton onClick={() => setIsExpanded(false)}>
						<KeyboardArrowUpIcon sx={{ color: '#4A5565' }} />
					</IconButton>
				)}
			</Stack>

			{isExpanded && (
				<Stack sx={{ gap: '5px' }}>
					{accessory.materials.map((material) => (
						<Stack
							key={material._id}
							sx={{
								height: '35px',
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'space-between',
								alignItems: 'center',
								flexShrink: 0,
								alignSelf: 'stretch',
								borderRadius: '6px',
								background: '#F9FAFB',
								padding: '0px 12px',
							}}
						>
							<Typography
								sx={{
									color: '#202937',
									flexShrink: 0,
									fontSize: '13px',
									fontFamily: 'Inter',
									lineHeight: '19.5px',
								}}
							>
								{material.name}
							</Typography>
							<Typography
								sx={{
									color: '#6A7181',
									flexShrink: 0,
									fontSize: '13px',
									fontFamily: 'Inter',
									lineHeight: '19.5px',
								}}
							>
								{material.unitsPerAccessory}
							</Typography>
						</Stack>
					))}
				</Stack>
			)}
		</Stack>
	);
};

export default AccessoryCard;
